import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import userSchema from '../../users.schema';
import useForm from '../../../../lib/hooks/useForm';
import { createUser } from '../../data';
import { InviteUser } from './InviteUser.jsx';
import { UserCreated } from './UserCreated.jsx';
import { useNotification } from '../../../../shared/Notification/useNotification';

export default function useCreateUserForm() {
  const currentBoard = useSelector(state => state.currentBoard);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const { mutate } = useMutation(createUser, {
    onSuccess: result => {
      queryClient.setQueryDefaults(['getUsers'], { enabled: true });
      queryClient.setQueryData(['getTotalUsers'], data => {
        return { totalUsers: data.totalUsers + 1 };
      });

      showSuccess({
        content: function UserCreatedNotification() {
          const user = result.data.user;
          const hasContactInfo = user.email || user.phoneNumber;

          return hasContactInfo ? (
            <InviteUser name={user.name} userId={user.id} />
          ) : (
            <UserCreated name={user.name} />
          );
        },
      });
    },
    onError: err => showError({ content: err.response.data.errors }),
  });

  const initialValues = {
    name: '',
    email: '',
    phoneNumber: '',
    isAdmin: false,
    populationSegments: [],
    employeeId: '',
  };

  const onSubmit = (values, { resetForm }) => {
    mutate({ user: values });
    resetForm();
  };

  const validate = async values => {
    try {
      await userSchema.validate(values, {
        abortEarly: false,
        context: { useEmployeeId: currentBoard.useEmployeeId },
      });
    } catch (e) {
      if (!e.inner) return {};
      return e.inner.reduce((acc, curr) => {
        if (curr.path && !acc[curr.path]) acc[curr.path] = curr.message;
        return acc;
      }, {});
    }
  };

  return useForm({
    initialValues,
    onSubmit,
    validate,
  });
}
